import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import Tooltip from 'antd/lib/tooltip';
import { Destination } from '@/types/destinations';
import { displayStringForDestination } from '@/helpers/displayHelpers';
import { DestinationIcon } from '../sidebar/shared/components';
import { useMdmPlatformOverride } from '@/helpers/temporaryMdmDisplayOverride';

interface DestinationCounterProps {
    destinations: Destination[];
    size?: number;
    countOnly?: boolean;
    hideTooltip?: boolean;
    platformLabelOverride?: string;
    platformIconOverride?: (size: number) => React.ReactNode;
}

export const DestinationCounter: React.FC<DestinationCounterProps> = ({
    destinations,
    size = 32,
    countOnly = false,
    hideTooltip = false,
    platformLabelOverride,
    platformIconOverride,
}) => {
    const mdmOverride = useMdmPlatformOverride(destinations[0]?.appId);
    const labelOverride = platformLabelOverride ?? mdmOverride?.label;
    const iconOverride = platformIconOverride ?? mdmOverride?.icon;

    const count = destinations.length;

    const fontSizeRatio = 1 / 2; // based on figma design
    const fontSize = size * fontSizeRatio;

    const borderRadiusRatio = 4.5 / 20; // based on figma design
    const borderRadius = size * borderRadiusRatio;

    // Tooltip content - only computed when needed
    const tooltipContent =
        hideTooltip ? undefined : (
            <div style={{ maxHeight: '50rem', overflowY: 'auto' }}>
                <div>{`${count} destination${count !== 1 ? 's' : ''}:`}</div>
                {count > 0 && (
                    <ul style={{ paddingLeft: 16, marginBottom: 0, marginTop: 4 }}>
                        {destinations.map((destination) => (
                            <li key={destination.stepConfigId}>
                                {displayStringForDestination(
                                    destination.platform,
                                    destination.appStoreType,
                                    destination.name,
                                    destination.appName,
                                    labelOverride,
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        );

    if (count === 0) {
        return null;
    } else if (count === 1 && !countOnly) {
        return (
            <Tooltip title={tooltipContent}>
                <>
                    <DestinationIcon destination={destinations[0]} size={size} platformIconOverride={iconOverride} />
                </>
            </Tooltip>
        );
    }

    const originalSizeToRoundedRectangleSizeRatio = 33.5 / 40; // based on figma design

    const isMoreThan99 = count > 99;
    return (
        <Tooltip title={tooltipContent}>
            <div
                style={{
                    position: 'relative',
                    display: 'block',
                    width: `${size}px`,
                    height: `${size}px`,
                    lineHeight: `${size}px`,
                    fontSize: fontSize,
                }}
            >
                <div
                    style={{
                        position: 'absolute',
                        width: `${size * originalSizeToRoundedRectangleSizeRatio}px`,
                        height: `${size * originalSizeToRoundedRectangleSizeRatio}px`,
                        lineHeight: `${size * originalSizeToRoundedRectangleSizeRatio - 2}px`,
                        borderRadius: borderRadius,
                        backgroundColor: 'var(--rw-dest-counter-bg-back)',
                        border: 'var(--rw-dest-counter-border)',
                        top: 0,
                        right: 0,
                    }}
                />
                <div
                    style={{
                        position: 'absolute',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: `${size * originalSizeToRoundedRectangleSizeRatio}px`,
                        height: `${size * originalSizeToRoundedRectangleSizeRatio}px`,
                        borderRadius: borderRadius,
                        backgroundColor: 'var(--rw-dest-counter-bg-front)',
                        border: 'var(--rw-dest-counter-border)',
                        bottom: 0,
                        left: 0,
                    }}
                >
                    <Text
                        style={{
                            fontSize: fontSize * (isMoreThan99 ? 0.6 : 1),
                            fontWeight: 600,
                            color: 'var(--rw-dest-counter-text)',
                        }}
                    >
                        {isMoreThan99 ? '99+' : count}
                    </Text>
                </div>
            </div>
        </Tooltip>
    );
};
